import type {
  PersistedToolProgressPayload,
  ToolProgressStatus,
  ToolProgressPayload,
} from "@/backend/analysis-chat-v2/types";

const TOOL_PROGRESS_STATUSES = new Set<ToolProgressStatus>([
  "running",
  "succeeded",
  "failed",
]);

const isToolProgressStatus = (
  status: string | undefined
): status is ToolProgressStatus =>
  status !== undefined &&
  TOOL_PROGRESS_STATUSES.has(status as ToolProgressStatus);

const toToolProgressStatus = (
  status: PersistedToolProgressPayload
): ToolProgressStatus => {
  if (isToolProgressStatus(status.status)) {
    return status.status;
  }
  return "done" in status && status.done ? "succeeded" : "running";
};

export const toToolProgressTimelineItem = (
  status: PersistedToolProgressPayload
): ToolProgressPayload | null => {
  if (!status.toolName) return null;
  return {
    id: status.id,
    timestamp: status.timestamp,
    status: toToolProgressStatus(status),
    toolName: status.toolName,
    ...(status.text ? { text: status.text } : {}),
    ...(status.details ? { details: status.details } : {}),
    ...(status.progress != null ? { progress: status.progress } : {}),
    ...("output" in status && status.output !== undefined
      ? { output: status.output }
      : {}),
  };
};

export const toToolProgressTimelineItems = (
  statuses: PersistedToolProgressPayload[]
): ToolProgressPayload[] =>
  statuses
    .filter((status) => !("debug" in status && status.debug))
    .flatMap((status) => {
      const item = toToolProgressTimelineItem(status);
      return item ? [item] : [];
    });

/**
 * Aggregates tool progress timeline items by ID, replacing existing ones with
 * the same ID or adding new ones.
 */
export const aggregateToolProgressTimelineItems = (
  existing: PersistedToolProgressPayload[],
  newStatus: ToolProgressPayload
): ToolProgressPayload[] => {
  const result = toToolProgressTimelineItems(existing);
  const existingIndex = result.findIndex((s) => s.id === newStatus.id);
  if (existingIndex !== -1) {
    result[existingIndex] = newStatus;
  } else {
    result.push(newStatus);
  }
  return result;
};
